import * as React from 'react';
import FeatureFlags from 'backend/view_models/feature_flags';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeIcon from '@mui/icons-material/Home';
import CelebrationIcon from '@mui/icons-material/Celebration';
import SettingsIcon from '@mui/icons-material/Settings';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PrintIcon from '@mui/icons-material/Print';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PageState, { MyRolesState } from '../../state/store';
import { UserCanSignupForShifts } from '../../state/users';
import { FeatureFlagsState } from '../../state/features';

interface MenuItemLinkData {
  text: string;
  index: string;
  icon: React.ReactNode;
  path: string;
  needsRole: string[];
  feature?: keyof FeatureFlags;
}

const mainLinks: MenuItemLinkData[] = [
  {
    text: 'Home',
    index: 'home',
    icon: <HomeIcon />,
    path: '/',
    needsRole: [],
  },
  {
    text: 'Shifts',
    index: 'shifts',
    icon: <CelebrationIcon />,
    path: '/shifts',
    needsRole: [],
  },
  {
    text: 'Final Assignments',
    index: 'final-assignments',
    icon: <PrintIcon />,
    path: '/final-assignments',
    needsRole: [],
    feature: 'chorePlanning',
  },
  {
    text: 'Roster',
    index: 'roser',
    icon: <BackupTableIcon />,
    path: '/roster',
    needsRole: [],
  },
];

const utilityLinks: MenuItemLinkData[] = [
  {
    text: 'Admin',
    index: 'admin',
    icon: <SupervisorAccountIcon />,
    path: '/admin',
    needsRole: ['admin'],
  },
  {
    text: 'Manage Users',
    index: 'admin-users',
    icon: <ManageAccountsIcon />,
    path: '/admin/manage-users',
    needsRole: ['admin'],
  },
  {
    text: 'Dues Management',
    index: 'admin-dues',
    icon: <AttachMoneyIcon />,
    path: '/admin/dues',
    needsRole: ['admin'],
  },
  {
    text: 'Manage Rosters',
    index: 'admin-rosters',
    icon: <EditCalendarIcon />,
    path: '/admin/rosters',
    needsRole: ['admin'],
  },
  {
    text: 'Chore Planner',
    index: 'admin-chore-planner',
    icon: <PlaylistAddCheckIcon />,
    path: '/admin/chore-planner',
    needsRole: ['admin'],
    feature: 'chorePlanning',
  },
  {
    text: 'Chore Scores',
    index: 'admin-chore-scores',
    icon: <PriceChangeIcon />,
    path: '/admin/chore-scores',
    needsRole: ['admin'],
    feature: 'chorePlanning',
  },
  {
    text: 'Profile Edit',
    index: 'profile-edit',
    icon: <SettingsIcon />,
    path: '/profile-edit',
    needsRole: [],
  },
];

export default function NavList() {
  const pageState = useRecoilValue(PageState);
  const myRoles = useRecoilValue(MyRolesState);
  const canSignupForShifts = useRecoilValue(UserCanSignupForShifts);
  const featureFlags = useRecoilValue(FeatureFlagsState);

  const isUserAllowed = (link: MenuItemLinkData) => {
    if (
      link.index === 'shifts' &&
      !featureFlags.chorePlanning &&
      !canSignupForShifts
    ) {
      return false;
    }

    if (link.feature && !featureFlags[link.feature]) {
      return false;
    }

    if (link.needsRole.length === 0) {
      return true;
    }
    return myRoles.find((role) => link.needsRole.includes(role.name));
  };

  const generateLinks = (links: MenuItemLinkData[]) =>
    links.map((link) => {
      if (isUserAllowed(link)) {
        return (
          <ListItemButton
            key={link.index}
            component={Link}
            to={link.path}
            selected={pageState.index === link.index}
          >
            <ListItemIcon>{link.icon}</ListItemIcon>
            <ListItemText primary={link.text} />
          </ListItemButton>
        );
      }
      return null;
    });

  const generateMainLinks = () => generateLinks(mainLinks);

  const generateUtilityLinks = () => generateLinks(utilityLinks);

  return (
    <List component="nav">
      {generateMainLinks()}
      <Divider sx={{ my: 1 }} />
      <>
        <ListSubheader component="div" inset>
          Settings
        </ListSubheader>
        {generateUtilityLinks()}
      </>
    </List>
  );
}
